import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Col, Form, Row } from "react-bootstrap";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import api from "../api/client";
import EmptyState from "../components/common/EmptyState";
import LoadingState from "../components/common/LoadingState";
import PageHeader from "../components/common/PageHeader";
import { loadActivityLookups } from "../utils/activityLookups";
import { getWorkplanYears } from "../utils/workplanYears";
import { downloadApiFile } from "../utils/download";

const MONTHS = [
  { key: 0, label: "Jan", quarter: "Q1" },
  { key: 1, label: "Feb", quarter: "Q1" },
  { key: 2, label: "Mar", quarter: "Q1" },
  { key: 3, label: "Apr", quarter: "Q2" },
  { key: 4, label: "May", quarter: "Q2" },
  { key: 5, label: "Jun", quarter: "Q2" },
  { key: 6, label: "Jul", quarter: "Q3" },
  { key: 7, label: "Aug", quarter: "Q3" },
  { key: 8, label: "Sep", quarter: "Q3" },
  { key: 9, label: "Oct", quarter: "Q4" },
  { key: 10, label: "Nov", quarter: "Q4" },
  { key: 11, label: "Dec", quarter: "Q4" }
];

const STATUS_OPTIONS = ["Pending", "In Progress", "Completed", "On Hold", "Not Done"];

function buildScheduledMonths(schedules, selectedYear) {
  return (schedules || [])
    .filter((schedule) => Number(schedule.year) === Number(selectedYear) && schedule.month_date)
    .map((schedule) => {
      const monthIndex = new Date(schedule.month_date).getMonth();
      return {
        monthIndex,
        status: schedule.status || "Pending"
      };
    })
    .sort((left, right) => left.monthIndex - right.monthIndex);
}

function getStatusClass(status) {
  switch (status) {
    case "Completed":
      return "gantt-cell gantt-cell-completed";
    case "In Progress":
      return "gantt-cell gantt-cell-progress";
    case "On Hold":
      return "gantt-cell gantt-cell-hold";
    case "Cancelled":
    case "Not Done":
      return "gantt-cell gantt-cell-cancelled";
    case "Pending":
    default:
      return "gantt-cell gantt-cell-pending";
  }
}

function WorkplanGanttPage() {
  const history = useHistory();
  const currentYear = new Date().getFullYear();
  const availableYears = getWorkplanYears(currentYear);
  const [lookups, setLookups] = useState({
    grants: [],
    implementors: [],
    pillars: []
  });
  const [filters, setFilters] = useState({
    year: currentYear,
    quarter: "",
    status: "",
    pillar_id: "",
    implementor_id: "",
    grant_id: ""
  });
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [downloadingGantt, setDownloadingGantt] = useState(false);

  useEffect(() => {
    fetchLookups();
  }, []);

  useEffect(() => {
    fetchActivities();
  }, [filters.year, filters.quarter, filters.status, filters.pillar_id, filters.implementor_id, filters.grant_id]);

  const rows = useMemo(() => {
    return activities
      .map((activity) => {
        const scheduledMonths = buildScheduledMonths(activity.schedules, filters.year);
        const firstMonth = scheduledMonths[0]?.monthIndex;
        const lastMonth = scheduledMonths[scheduledMonths.length - 1]?.monthIndex;

        return {
          ...activity,
          scheduledMonths,
          firstMonth,
          lastMonth
        };
      })
      .sort((left, right) => {
        if (left.firstMonth !== right.firstMonth) {
          return (left.firstMonth ?? 99) - (right.firstMonth ?? 99);
        }

        return String(left.name || "").localeCompare(String(right.name || ""));
      });
  }, [activities, filters.year]);

  async function fetchLookups() {
    const lookupData = await loadActivityLookups();
    setLookups({
      grants: lookupData.grants,
      implementors: lookupData.implementors,
      pillars: lookupData.pillars
    });
  }

  async function fetchActivities() {
    setLoading(true);
    setError("");

    try {
      const collectedItems = [];
      let page = 1;
      let totalPages = 1;

      while (page <= totalPages) {
        const response = await api.get("/activities", {
          params: {
            year: filters.year,
            quarter: filters.quarter || undefined,
            status: filters.status || undefined,
            pillar_id: filters.pillar_id || undefined,
            implementor_id: filters.implementor_id || undefined,
            grant_id: filters.grant_id || undefined,
            page,
            per_page: 200
          }
        });

        const payload = response.data || {};
        collectedItems.push(...(payload.items || []));
        totalPages = payload.total_pages || 1;
        page += 1;
      }

      setActivities(collectedItems);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to load workplan gantt chart.");
      setActivities([]);
    } finally {
      setLoading(false);
    }
  }

  function handleFilterChange(event) {
    const { name, value } = event.target;
    setFilters((current) => ({
      ...current,
      [name]: value
    }));
  }

  function handleClearFilters() {
    setFilters({
      year: currentYear,
      quarter: "",
      status: "",
      pillar_id: "",
      implementor_id: "",
      grant_id: ""
    });
  }

  async function handleDownloadGantt() {
    setDownloadingGantt(true);

    try {
      await downloadApiFile("/activities/export/gantt", {
        params: {
          year: filters.year,
          quarter: filters.quarter || undefined,
          status: filters.status || undefined,
          pillar_id: filters.pillar_id || undefined,
          implementor_id: filters.implementor_id || undefined,
          grant_id: filters.grant_id || undefined
        },
        fallbackFileName: `gantt-${filters.year}.csv`
      });
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to download gantt export.");
    } finally {
      setDownloadingGantt(false);
    }
  }

  function renderTimelineRow(activity) {
    const monthMap = new Map(activity.scheduledMonths.map((item) => [item.monthIndex, item.status]));

    return (
      <div className="gantt-timeline-grid" key={activity.uuid}>
        {MONTHS.map((month) => {
          const status = monthMap.get(month.key);

          return (
            <div
              key={`${activity.uuid}-${month.key}`}
              className={status ? getStatusClass(status) : "gantt-cell"}
              title={status ? `${month.label} - ${status}` : month.label}
            >
              <span className="visually-hidden">{status ? `${month.label} ${status}` : month.label}</span>
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Workplan Gantt Chart"
        action={
          <div className="d-flex gap-2">
            <Button
              variant="primary"
              onClick={handleDownloadGantt}
              disabled={downloadingGantt || loading}
              title="Download gantt chart"
            >
              {downloadingGantt ? "Downloading..." : "Download"}
            </Button>
            <Button variant="outline-secondary" onClick={handleClearFilters}>
              Clear filters
            </Button>
          </div>
        }
      />

      <Row className="g-4">
        <Col xs={12}>
          <div className="content-card table-card">
            <Row className="g-3 mb-3">
              <Col md={6} xl={2}>
                <label className="form-label">Filter year</label>
                <Form.Select name="year" value={filters.year} onChange={(event) => setFilters((current) => ({ ...current, year: Number(event.target.value) }))}>
                  {availableYears.map((itemYear) => (
                    <option key={itemYear} value={itemYear}>
                      {itemYear}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={6} xl={2}>
                <label className="form-label">Quarter</label>
                <Form.Select name="quarter" value={filters.quarter} onChange={handleFilterChange}>
                  <option value="">All quarters</option>
                  <option value="Q1">Q1</option>
                  <option value="Q2">Q2</option>
                  <option value="Q3">Q3</option>
                  <option value="Q4">Q4</option>
                </Form.Select>
              </Col>
              <Col md={6} xl={2}>
                <label className="form-label">Status</label>
                <Form.Select name="status" value={filters.status} onChange={handleFilterChange}>
                  <option value="">All statuses</option>
                  {STATUS_OPTIONS.map((status) => (
                    <option key={status} value={status}>
                      {status}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={6} xl={2}>
                <label className="form-label">Pillar</label>
                <Form.Select name="pillar_id" value={filters.pillar_id} onChange={handleFilterChange}>
                  <option value="">All pillars</option>
                  {lookups.pillars.map((pillar) => (
                    <option key={pillar.id} value={pillar.id}>
                      {pillar.name}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={6} xl={2}>
                <label className="form-label">Implementor</label>
                <Form.Select name="implementor_id" value={filters.implementor_id} onChange={handleFilterChange}>
                  <option value="">All implementors</option>
                  {lookups.implementors.map((implementor) => (
                    <option key={implementor.id} value={implementor.id}>
                      {implementor.name}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={6} xl={2}>
                <label className="form-label">Grant</label>
                <Form.Select name="grant_id" value={filters.grant_id} onChange={handleFilterChange}>
                  <option value="">All grants</option>
                  {lookups.grants.map((grant) => (
                    <option key={grant.id} value={grant.id}>
                      {grant.name}
                    </option>
                  ))}
                </Form.Select>
              </Col>
            </Row>

            <div className="gantt-summary-bar">
              <div className="activity-total-count">Total activities: {rows.length}</div>
              <div className="gantt-legend">
                <span className="gantt-legend-item"><span className="gantt-legend-swatch gantt-cell-pending"></span> Pending</span>
                <span className="gantt-legend-item"><span className="gantt-legend-swatch gantt-cell-progress"></span> In Progress</span>
                <span className="gantt-legend-item"><span className="gantt-legend-swatch gantt-cell-completed"></span> Completed</span>
                <span className="gantt-legend-item"><span className="gantt-legend-swatch gantt-cell-hold"></span> On Hold</span>
                <span className="gantt-legend-item"><span className="gantt-legend-swatch gantt-cell-cancelled"></span> Not Done</span>
              </div>
            </div>

            {loading ? <LoadingState label="Loading workplan gantt chart..." /> : null}
            {error ? <Alert variant="danger">{error}</Alert> : null}
            {!loading && !error && !rows.length ? (
              <EmptyState title="No activities found" text="Activities matching the selected filters will appear here." />
            ) : null}

            {!loading && rows.length ? (
              <div className="gantt-board">
                <div className="gantt-header-row">
                  <div className="gantt-activity-header">Activity</div>
                  <div className="gantt-timeline-header-wrap">
                    <div className="gantt-quarter-row">
                      <div className="gantt-quarter-cell">Q1</div>
                      <div className="gantt-quarter-cell">Q2</div>
                      <div className="gantt-quarter-cell">Q3</div>
                      <div className="gantt-quarter-cell">Q4</div>
                    </div>
                    <div className="gantt-month-row">
                      {MONTHS.map((month) => (
                        <div key={month.label} className="gantt-month-cell">
                          {month.label}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {rows.map((activity) => (
                  <div className="gantt-data-row" key={activity.uuid}>
                    <div className="gantt-activity-cell">
                      <button
                        type="button"
                        className="activity-link"
                        onClick={() => history.push(`/activities/${activity.uuid}`)}
                      >
                        <div className="gantt-activity-name">{activity.name}</div>
                      </button>
                    </div>
                    {renderTimelineRow(activity)}
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        </Col>
      </Row>
    </div>
  );
}

export default WorkplanGanttPage;
